import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Search, Check, X } from "lucide-react";
import { api } from "@/lib/api";

export const scoreTone = (n) => (n >= 85 ? "text-emerald-300 border-emerald-400/40 bg-emerald-500/10" : n >= 70 ? "text-sky-300 border-sky-400/40 bg-sky-500/10" : n >= 50 ? "text-amber-300 border-amber-400/40 bg-amber-500/10" : "text-rose-300 border-rose-400/40 bg-rose-500/10");

export const ScorePill = ({ label, value, testId }) => (
  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.7rem] font-mono ${scoreTone(value)}`} data-testid={testId} title={`${label} score`}>{label} {value}</span>
);

const Card = ({ icon: Icon, title, data, children, testId }) => (
  <div className={`rounded-xl border p-3 ${scoreTone(data.score)}`} data-testid={testId}>
    <div className="flex items-center justify-between gap-2">
      <p className="eyebrow text-[0.65rem] flex items-center gap-1.5 !text-inherit"><Icon size={12} /> {title}</p>
      <p className="font-display text-2xl font-black leading-none" data-testid={`${testId}-value`}>{data.score}<span className="text-xs font-sans font-normal opacity-70">/100</span></p>
    </div>
    <p className="text-sm font-semibold mt-0.5" data-testid={`${testId}-grade`}>{data.grade}</p>
    {children}
  </div>
);

export const ScorePanel = ({ form, art }) => {
  const [data, setData] = useState({ quality: art.quality, seo: art.seo });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.post("/admin/articles/score", { headline: form.headline, standfirst: form.standfirst, body_md: form.body_md, slug: art.slug, show_name: art.show_name, query: art.seo?.query || "",
          sources: form.sources.split("\n").filter((l) => l.trim()).map((l) => ({ title: l, url: l })), faq: [...(form.faq || "").matchAll(/Q:\s*(.+?)\s*\n\s*A:\s*([\s\S]+?)(?=\n\s*Q:|$)/g)].map((m) => ({ q: m[1], a: m[2] })) });
        setData(r.data);
      } catch (_) { /* keep last score */ } finally { setBusy(false); }
    }, 900);
    return () => clearTimeout(t);
  }, [form.headline, form.standfirst, form.body_md, form.sources, form.faq]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data.quality || !data.seo) return null;
  return (
    <div className="space-y-2" data-testid="score-panel">
      <div className="grid grid-cols-2 gap-2">
        <Card icon={ShieldCheck} title="AI-tell check" data={data.quality} testId="quality-score">
          <p className="text-xs opacity-80 mt-1">{data.quality.flags.length ? `${data.quality.flags.length} flag${data.quality.flags.length > 1 ? "s" : ""}` : "No tells found"} · {data.quality.stats.sentences} sentences, burstiness {data.quality.stats.burstiness}</p>
        </Card>
        <Card icon={Search} title="SEO check" data={data.seo} testId="seo-score">
          <p className="text-xs opacity-80 mt-1">{data.seo.checks.filter((c) => c.pass).length}/{data.seo.checks.length} checks pass{busy && <Loader2 size={11} className="inline ml-1 animate-spin" />}</p>
        </Card>
      </div>
      <button onClick={() => setOpen(!open)} className="text-xs text-sky-300 hover:text-white" data-testid="score-details-toggle">{open ? "Hide details" : "Show what to fix"}</button>
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <ul className="space-y-1.5" data-testid="quality-flags">
            {data.quality.flags.length === 0 && <li className="text-emerald-300 text-xs">Nothing flagged — phrases, rhythm and structure all read human.</li>}
            {data.quality.flags.map((f, i) => <li key={i} className="rounded-lg bg-white/5 px-3 py-2" data-testid="quality-flag"><p className={`font-semibold text-xs ${f.severity === "high" ? "text-rose-300" : f.severity === "medium" ? "text-amber-300" : "text-slate-200"}`}>{f.label}</p><p className="text-xs text-slate-400">{f.detail}</p></li>)}
          </ul>
          <ul className="space-y-1" data-testid="seo-checks">
            {data.seo.checks.map((c) => <li key={c.label} className="flex items-start gap-2 text-xs" data-testid="seo-check" data-pass={c.pass}>{c.pass ? <Check size={13} className="text-emerald-400 mt-0.5 shrink-0" /> : <X size={13} className="text-rose-400 mt-0.5 shrink-0" />}<span className={c.pass ? "text-slate-300" : "text-white"}>{c.label}{c.detail && <span className="text-slate-500"> · {c.detail}</span>}</span></li>)}
          </ul>
        </div>
      )}
    </div>
  );
};
